// 标签汇总页「组合表现」图的 ECharts option（AC-109）。配色同 components/AssetPerformanceChart.jsx。
import { chartAxisNumber, chartNumber } from "../../utils/format.js";
import { alignDatedValues, rebaseComparisonValues } from "../../utils/performanceComparison.js";

const COLORS = { tag: "#354052", benchmark: "#356dff", assets: "#354052", invested: "#c77914" };

function pct(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${(numeric * 100).toLocaleString("zh-CN", { maximumFractionDigits: 2 })}%` : "--";
}

// 净值序列相对区间内历史峰值的回撤，恒 ≤ 0；缺点保持空缺。
function drawdownOf(values) {
  let peak = -Infinity;
  return values.map((value) => {
    if (!Number.isFinite(value)) return null;
    peak = Math.max(peak, value);
    return value / peak - 1;
  });
}

function lineSeries(name, color, data, { dashed = false, step = false } = {}) {
  return {
    name, type: "line", data, showSymbol: false, connectNulls: false, smooth: false,
    step: step ? "end" : false,
    lineStyle: { color, width: 2, type: dashed ? "dashed" : "solid" },
    itemStyle: { color },
  };
}

// 把组合的某个字段摆到 dates 上：第一个点之前（还没买入）补 leading，之后缺的日子保持空缺。
function ownValues(series, dates, pick, leading) {
  const points = series.map((point) => ({ date: point.date, value: pick(point) }));
  return alignDatedValues(points, dates, { leadingValue: leading });
}

// series: 接口的逐日点；view: ret | dd | value；benchmark: [{date, value}]（指数点位，仅 ret / dd 用）。
// dates: 图上显示的日子（区间内），可以比 series 起点更早——更早的部分组合还没有持仓：
// 收益率 / 回撤补 0（平线），市值与投入补 0；所有线都以 dates 的第一天为起点重新计价。
// 不传 dates 就用 series 自己的日子。
export function tagChartOption(series, view, benchmark = [], dates = null) {
  if (!series?.length) return null;
  dates = dates ?? series.map((point) => point.date);
  if (!dates.length) return null;
  const isPct = view !== "value";
  let lines;
  if (view === "value") {
    lines = [
      lineSeries("总资产", COLORS.assets, ownValues(series, dates, (p) => Number(p.total_asset_value), 0)),
      // 投入是台阶：外部资金进来那天才变，用阶梯线
      lineSeries("累计投入", COLORS.invested, ownValues(series, dates, (p) => Number(p.cumulative_invested), 0), { step: true }),
    ];
  } else {
    const nav = ownValues(series, dates, (point) => 1 + Number(point.nav_return), 1);
    const benchValues = alignDatedValues(benchmark, dates);
    const hasBenchmark = benchValues.some(Number.isFinite);
    const own = view === "dd" ? drawdownOf(nav) : rebaseComparisonValues(nav);
    lines = [lineSeries("标签组合", COLORS.tag, own)];
    if (hasBenchmark) {
      const other = view === "dd" ? drawdownOf(benchValues) : rebaseComparisonValues(benchValues);
      lines.push(lineSeries("沪深300", COLORS.benchmark, other, { dashed: true }));
    }
  }
  return {
    animation: false,
    grid: { left: 56, right: 22, top: 18, bottom: 34 },
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const items = Array.isArray(params) ? params : [];
        const rows = items.map((item) => `${item.marker}${item.seriesName}：${isPct ? pct(item.value) : chartNumber(item.value)}`);
        return [`<strong>${items[0]?.axisValue ?? "—"}</strong>`, ...rows].join("<br/>");
      },
    },
    xAxis: { type: "category", boundaryGap: false, data: dates, axisLine: { onZero: false },
      axisLabel: { formatter: (value) => String(value).slice(0, 7), hideOverlap: true } },
    yAxis: {
      type: "value", scale: view !== "dd",
      // 回撤恒 ≤ 0：上沿钉在 0，别让轴顶出一截正数
      ...(view === "dd" ? { max: 0 } : {}),
      axisLabel: { formatter: isPct ? pct : chartAxisNumber },
      splitLine: { lineStyle: { color: "#edf2ef" } },
    },
    series: lines,
  };
}

function ratio(values) {
  const finite = values.filter(Number.isFinite);
  if (finite.length < 2) return null;
  return finite.at(-1) / finite[0] - 1;
}

// 图例旁那一行摘要（原型 v23）：收益率 = 区间内组合 / 沪深300 / 超额；回撤 = 最大回撤（日期）/ 当前回撤 / 沪深300 最大回撤。
// dates 同 tagChartOption（区间内的日子，可早于 series 起点）；任何一项算不出来（点数不足、没有沪深300）都是 null，页面显示「--」。
export function tagViewSummary(series, view, benchmark = [], dates = null) {
  const empty = { own: null, bench: null, excess: null, maxDrawdown: null, maxDrawdownDate: "", currentDrawdown: null, benchMaxDrawdown: null };
  if (!series?.length || view === "value") return empty;
  dates = dates ?? series.map((point) => point.date);
  if (!dates.length) return empty;
  const nav = ownValues(series, dates, (point) => 1 + Number(point.nav_return), 1);
  const benchValues = alignDatedValues(benchmark, dates);
  if (view === "ret") {
    const own = ratio(nav);
    const bench = ratio(benchValues);
    return { ...empty, own, bench, excess: own !== null && bench !== null ? own - bench : null };
  }
  const dd = drawdownOf(nav);
  const finite = dd.filter(Number.isFinite);
  const maxDrawdown = finite.length ? Math.min(...finite) : null;
  const benchFinite = drawdownOf(benchValues).filter(Number.isFinite);
  return {
    ...empty,
    maxDrawdown,
    maxDrawdownDate: maxDrawdown === null ? "" : dates[dd.indexOf(maxDrawdown)],
    currentDrawdown: finite.length ? finite.at(-1) : null,
    benchMaxDrawdown: benchFinite.length ? Math.min(...benchFinite) : null,
  };
}
